import {
  AlertColor,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { globalTheme } from "../../../common/theme";
import createStyles from "@mui/styles/createStyles";
import axios from "axios";
import React, { useImperativeHandle } from "react";
import { Package, ProductListItem } from "./ProductAccessories";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import Title from "../../component/Title";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { GridColumns, GridSelectionModel, GridToolbarContainer } from "@mui/x-data-grid";
import { CustomTablePagination } from "../../component/CustomPagination";
import { Cancel, CheckCircle, Inventory, Search } from "@mui/icons-material";
import { ProductPackageList, ProductPackageListHandle } from "./ProductPackageList";
import { renewToken } from "../../../services/AuthService";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
  })
);

export interface ProductListHandle {
  Initialise: () => void;
}

export interface ProductListProps {
  onClose: Function;
}

export const ProductList = React.forwardRef(
  (props: ProductListProps, ref: React.Ref<ProductListHandle>) => {
    const style = { ...useStyles(), ...globalTheme() };

    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');

    const singleModalRef = React.useRef<ProductPackageListHandle>(null);
    const [packageList, setPackageList] = React.useState<Package[]>([]);

    const [open, setOpen] = React.useState(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    
    const [gridData, setGridData] = React.useState<ProductListItem[]>([]);
    const [selectedGridData, setSelectedGridData] = React.useState<ProductListItem[]>([]);
    const [selectionModel, setSelectionModel] = React.useState(() =>
      gridData.filter((r) => r.selected).map((r) => r.id),
    );
    //Grid columns
    const columns: GridColumns = [
      { field: 'nameJpn', headerName: '製品名(日本語)', flex: 5, type: 'string', editable: true },
      { field: 'nameChi', headerName: '製品名(中囯語)', flex: 4, type: 'string', editable: true },
      { field: 'nameEng', headerName: '製品名(英語)', flex: 4, type: 'string', editable: true },
      { field: 'description', headerName: '針長', flex: 2.5, type: 'string', editable: true },
      { field: 'model', headerName: '型式', flex: 4, type: 'string', editable: true },
      { field: 'productCode', headerName: '商品CD', flex: 3, type: 'string', editable: true },
      { field: 'price', headerName: '標準価格', flex: 1.5, type: 'number' },
      { field: 'unit', headerName: '单位', flex: 1, type: 'string' },
      { field: 'minOrderQty', headerName: '最少数', flex: 1, type: 'number', minWidth: 40 },
      { field: 'newCDCode', headerName: '品目CD', flex: 3, type: 'string', editable: true },
      { field: 'scaw', headerName: 'SCAW', flex: 1.5, type: 'string' },
      { field: 'remark', headerName: '备註', flex: 3, type: 'string', editable: true }
    ];

    const [searchData, setSearchData] = React.useState<any>({
      pageSize: 15,
      currentPage: 0
    });

    const selectedProducts = React.useRef<ProductListItem[]>([]);
    
    //Dialogs and snacks
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }

    function OnSelectionChanged(model: GridSelectionModel) {
      const ids = new Set(model);
      const selectedRows = gridData.filter((row) => ids.has(row.productID));
      selectedProducts.current = selectedRows;
      gridData.forEach(item => item.selected = ids.has(item.productID));
      setSelectedGridData(selectedRows);

      //Float all selected rows to first page
      let tempGridData = [...gridData];
      setGridData(tempGridData.sort((a, b) => {
        if (a.selected > b.selected) { return -1; }
        if (a.selected < b.selected) { return 1; }
        return 0;
      }));

      setSearchData({ ...searchData, pageSize: searchData.pageSize, currentPage: 0 })

      setSelectionModel(() =>
        gridData.filter((r) => r.selected).map((r) => r.id));

    }

    function CustomToolbar() {
      const [inputText, setInputText] = React.useState("");
      return (
        <GridToolbarContainer style={{ display: 'flex' }}>
          <TextField className={style.ModalContentField}
            style={{ minWidth: '100px', width: '100%' }}
            label="商品CD/型式/製品名/SCAW/PO No./QT No."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => KeyPressed(e.key, inputText)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => {
                      SearchProduct(inputText);
                    }}><Search />
                  </IconButton>
                </InputAdornment>
              ),
            }}   
          />
          <div style={{ flexGrow: 1, minWidth: "10px" }}></div>
          <Button style={{ width: "120px", minWidth: "100px" }}
            variant="contained" size="small" color="secondary" startIcon={<Inventory />} onClick={() => OpenPackage()}>Package</Button>
          <div style={{ width: "10px", minWidth: "10px" }}></div>
          <Button style={{ width: "120px", minWidth: "100px" }}
            variant="contained" size="small" color="secondary" startIcon={<CheckCircle />} onClick={() => CloseModal(false)}>Select</Button>
          <div style={{ width: "10px", minWidth: "10px" }}></div>
          <Button style={{ width: "120px", minWidth: "100px" }}
            variant="contained" size="small" color="secondary" startIcon={<Cancel />} onClick={(e) => CloseModal(true)}>Cancel</Button>
          <div style={{ width: "10px", minWidth: "10px" }}></div>
        </GridToolbarContainer>
      );
    }

    function OpenPackage() {
      singleModalRef.current?.Initialise();
    }

    function KeyPressed(key: string, inputText: string) {
      if(key === 'Enter'){
        SearchProduct(inputText);
      }      
    }

    function SearchProduct(inputText: string) {
      setLoading(true);

      const params = { searchString: inputText, excludeNoUse: true };
      axios.get<ProductListItem[]>('Api/V1/Product/Search', {
        params,
        headers: {
          'Content-Encoding': 'gzip'
        }
      })
        .then((result) => {
          result.data.forEach(i => {
            i.id = i.productID;
            i.selected = false;
          });
          let tmpData = [...result.data];
          selectedGridData.forEach(
            item => {
              let itemIndex = tmpData.findIndex(x => x.productID == item.productID);
              if (itemIndex == -1)
              {
                result.data.push(item);
              }
              else
              {
                result.data[itemIndex].selected = true; 
              }
            }
          );
          setGridData(result.data.sort((a, b) => {
            if (a.selected > b.selected) { return -1; }
            if (a.selected < b.selected) { return 1; }
            return 0;
          }));
          setLoading(false);
        },
          (error) => {
            setGridData([]);
            setLoading(false);
            console.log(error);
            showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
          }
        )
        .catch((error) => {
          setLoading(false);
          console.log(error);
          showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
        });
    }

    function LoadPackageList() {
      setLoading(true);

      const params = { searchString: "" };
      axios.get<Package[]>('Api/V1/Package/Search', {
        params,
        headers: {
          'Content-Encoding': 'gzip'
        }
      })
        .then((result) => {
          result.data.forEach(i => {
            i.id = i.packageID;
          });
          setPackageList(result.data)
          setLoading(false);
        },
          (error) => {
            setPackageList([]);
            setLoading(false);
            console.log(error);
            showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
          }
        )
        .catch((error) => {
          setLoading(false);
          console.log(error);
          showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
        });
    }

    useImperativeHandle(ref, () => ({
      Initialise() {
        setOpen(true);
        renewToken();
        
        selectedProducts.current = [];
        setSelectionModel([]);
        setSelectedGridData([]);
        SearchProduct("");
        LoadPackageList();
        gridData.forEach((x) => x.selected = false);
      },
    }));

    function CloseModal(isCancel: boolean) {
      setOpen(false);
      if (isCancel)
        selectedProducts.current = [];

      props.onClose(!isCancel, selectedProducts);
    }

    function PopulateSelectedProductPackageItems(isCancel: boolean, selectedProductPackageItems: React.MutableRefObject<ProductListItem[]>) {
      if (isCancel)
        return;


      let tempGridData: ProductListItem[] = [...selectedProductPackageItems.current, ...gridData];

      setGridData(tempGridData.sort((a, b) => {
        if (a.selected > b.selected) { return -1; }
        if (a.selected < b.selected) { return 1; }
        return 0;
      }));

      const selectedRows = tempGridData.filter((row) => row.selected);
      selectedProducts.current = selectedRows;

      setSelectedGridData(selectedRows);
      setSelectionModel(() => selectedRows.map((r) => r.id));
    }

    return (
      <div>
        <Modal open={open} onClose={() => CloseModal(true)}>
          <div className={style.ModalMain}>
            {!loading ? (
              <div className={style.ModalBody}>
                <Title pageTitle="Product List" />

                <div className={style.ModalHeading}>
                  <Typography variant="h4">Product List</Typography>
                </div>

                <div className={style.ModalContent}>
                  <div className={style.PageContent} style={{ minHeight: "400px" }}>
                    <DataGridPro
                      getRowId={(row) => row.id}
                      rows={gridData}
                      columns={columns}
                      pageSize={searchData.pageSize}
                      onPageSizeChange={(pageSize) => setSearchData({ ...searchData, pageSize: pageSize, currentPage: 0 })}
                      pagination
                      page={searchData.currentPage}
                      onPageChange={(page) => setSearchData({ ...searchData, pageSize: searchData.pageSize, currentPage: page })}
                      rowsPerPageOptions={[15, 30, 50, 100]}
                      loading={loading}
                      checkboxSelection
                      components={{
                        Toolbar: CustomToolbar,
                        Footer: CustomTablePagination
                      }}
                      componentsProps={{
                        footer: { rowsPerPageOptions: [15, 30, 50, 100] }
                      }}
                      selectionModel={selectionModel}
                      onSelectionModelChange={(selectionModel) => {
                        OnSelectionChanged(selectionModel)
                      }}
                      rowHeight={tinyScreen ? 20 : (smallScreen ? 24 : 28)}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: "center" }}>
                <CircularProgress color="primary" />
              </div>
            )}
          </div>
        </Modal>

        <SnackBarMessage {...snackSettings} />

        <ProductPackageList
          productPackages={packageList}
          ref={singleModalRef}
          onClose={(isCancel: boolean, selectedProductPackageItems: React.MutableRefObject<ProductListItem[]>) => PopulateSelectedProductPackageItems(isCancel, selectedProductPackageItems)}
        />

      </div>
    );


  }

);
